import { Theme } from "@mui/material/styles";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AddCircleOutline, ArrowDropDown, ArrowDropUp, Close, RestartAlt } from "@mui/icons-material";
import { CircularProgress, Grid, IconButton, InputAdornment, List, ListItem, debounce } from "@mui/material";

import Box from "../Box";
import FormField from "../FormField";
import Typography from "../Typography";
import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import Resources from "../../constants/resources";

import { ICodename } from "../../commons/interfaces";
import { Constants } from "../../constants/constants";
import { box, container, listItem, scrollBox } from "./styles";

interface IProps {
    multiple?: boolean;
    disabled?: boolean;
    isSelectBox?: boolean;
    isSearchOrder?: boolean;
    isAutocompleteMode?: boolean;

    variant?: "standard" | "outlined";
    label?: string;
    placeholder?: string;
    errorMessage?: string;

    requestGetApiDefault?: any;
    dataOptionInit?: ICodename[];
    getApi: (data: any) => Promise<{
        data: any[],
        hasNext: boolean,
        currentPage: number,
    }>;
    debounceTime?: number;

    loadActionMore?: () => void;
    actionCreateNewItem?: () => void;
    onChangeValue: (value: any) => void;
    onClick?: (value: ICodename) => void;
    [key: string]: any;
}

const SearchInputAPI = ({
    isAutocompleteMode,

    multiple,
    disabled,
    isSelectBox,
    isSearchOrder,

    label,
    variant,
    placeholder,
    errorMessage,

    debounceTime = 1000,
    getApi,
    dataOptionInit,
    requestGetApiDefault,

    actionCreateNewItem,
    loadActionMore,
    onChangeValue,
    onClick,
    ...rest
}: IProps) => {
    const strings = useStrings();
    const wrapperRef = useRef<any>(null);

    const [loading, setLoading] = useState<boolean>(false);
    const [visible, setVisible] = useState<boolean>(false);

    const [valueSelect, setValueSelect] = useState<string>("");
    const [dataSelect, setDataSelect] = useState<ICodename[]>([...dataOptionInit || []]);

    const [requestData, setRequestData] = useState({
        currentPage: 0,
        hasNext: false,
    });

    const handleSearchValue = (value: any) => {
        setLoading(true);
        setVisible(true);
        setValueSelect(value);
        deboundGetDataByApi(value);
        Helpers.isFunction(onChangeValue) && onChangeValue(value);
    };

    const handleClickItem = (item: any) => {
        setVisible(false);
        setValueSelect(item.name);
        Helpers.isFunction(onClick) && onClick(item);
        setLoading(false);
    };

    const handleClickAddNewItem = () => {
        setVisible(false);
        Helpers.isFunction(actionCreateNewItem) && actionCreateNewItem();
    };

    const handleClickLoadMore = async () => {
        if (requestData.hasNext) {
            setLoading(true);
            const resultData = await getDataByApi(valueSelect, requestData.currentPage + 1);
            setDataSelect(dataSelect.concat(resultData));
            setLoading(false);
        }
    };

    const getDataByApi = useCallback(async (searchText: string, pageNumber?: number) => {
        let currentRequestData: typeof requestData = undefined;
        setRequestData(o => {
            currentRequestData = o;
            return o;
        })

        let requestData: any = {
            // @ts-ignore
            ...currentRequestData,
            pageNumber: pageNumber || 1,
            pageSize: Constants.ROW_PER_PAGE,
            searchText: searchText || undefined,
        };

        const result = await getApi(requestData);

        setRequestData({
            currentPage: result?.currentPage || 0,
            hasNext: result?.hasNext || false,
        });

        return result?.data || [];
    }, [getApi]);

    const currentSearchTextRef = useRef<string>("");
    const deboundGetDataByApi = useMemo(() => debounce(async (nextValue: string) => {
            try {
                if (currentSearchTextRef.current === nextValue) {
                    return;
                }
                currentSearchTextRef.current = nextValue;
                const resultData = await getDataByApi(nextValue);

                if (currentSearchTextRef.current !== nextValue) {
                    return;
                }
                setLoading(false);
                setDataSelect(resultData);
            } catch (error) {
                setVisible(false);
                setLoading(false);
            }
        }, debounceTime),
        [getDataByApi, debounceTime]
    );

    function handleClickOutside(event: any) {
        if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
            setVisible(false);
            !Helpers.isNullOrEmpty(valueSelect) ? setValueSelect(valueSelect) : setValueSelect("");
        }
    }

    useEffect(() => {
        valueSelect && deboundGetDataByApi("");
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [wrapperRef]);

    const renderProductItems = (row: ICodename, index: number) => (
        <ListItem key={index} sx={(theme: Theme) => listItem(theme, { isOrder: true })} onClick={() => handleClickItem(row)}>
            <Grid item xs={2}>
                <Typography variant="button">
                    <Box
                        component={"img"}
                        src={
                            Helpers.isNullOrEmpty(row?.detail?.photoId) ? Resources.IMAGES.THUMBNAIL : Helpers.getFileAccessUrl(row?.detail?.photoId)
                        }
                        sx={{ width: "30px !important", height: "30px !important", borderRadius: "3px", objectFit: "cover" }}
                    />
                </Typography>
            </Grid>
            <Grid item xs={5}>
                <Typography variant="button">{row.name}</Typography>
            </Grid>
            <Grid item xs={3}>
                <Typography variant="button">{Helpers.formatCurrency(Number(row?.detail?.price), 2)}</Typography>
            </Grid>
        </ListItem>
    );

    return (
        <Box sx={container} ref={wrapperRef}>
            <FormField
                fullWidth
                key={label}
                label={label}
                variant={variant}
                disabled={disabled}
                placeholder={placeholder}
                helperText={errorMessage}
                InputLabelProps={{ shrink: true }}
                error={!Helpers.isNullOrEmpty(errorMessage)}
                onChange={(event: any) => handleSearchValue(event.target.value)}
                onClick={(event: any) => {
                    if (dataSelect.length > 0) {
                        setVisible(true);
                    }
                }}
                onFocus={(event: any) => event.target.select()}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            {loading && (
                                <CircularProgress
                                    style={{
                                        color: "#3b82f6",
                                        width: 18,
                                        height: 18,
                                        marginRight: 4,
                                    }}
                                />
                            )}
                            {isAutocompleteMode && (
                                <>
                                    {!isSelectBox && !Helpers.isNullOrEmpty(rest.value) && (
                                        <IconButton
                                            color="secondary"
                                            onClick={() => {
                                                handleSearchValue("");
                                                Helpers.isFunction(onChangeValue) && onChangeValue("");
                                            }}
                                        >
                                            <Close />
                                        </IconButton>
                                    )}
                                    <IconButton
                                        color="secondary"
                                        onClick={() => {
                                            if (visible === false) {
                                                setVisible(true);
                                            } else {
                                                setVisible(false);
                                            }
                                        }}
                                    >
                                        {visible ? <ArrowDropUp /> : <ArrowDropDown />}
                                    </IconButton>
                                </>
                            )}
                        </InputAdornment>
                    ),
                }}
                {...rest}
            />
            {visible && dataSelect?.length > 0 && (
                <Grid container sx={box}>
                    {dataSelect.length > 0
                        ? (
                            <Grid item xs={12} sx={scrollBox}>
                                {isSearchOrder
                                    ? (
                                        <Grid container>{dataSelect?.map((row: any, index: number) => renderProductItems(row, index))}</Grid>
                                    )
                                    : (
                                        <List>
                                            {dataSelect.map((el: ICodename, index: number) => (
                                                <ListItem
                                                    key={index}
                                                    sx={(theme: Theme) => listItem(theme, { isAdd: false, isOrder: false })}
                                                    onClick={() => handleClickItem(el)}
                                                >
                                                    <Typography
                                                        sx={{
                                                            margin: 0,
                                                            opacity: 1,
                                                            lineHeight: 1.25,
                                                            color: "#7b809a",
                                                            fontSize: "0.85rem",
                                                            textTransform: "none",
                                                            verticalAlign: "unset",
                                                            letterSpacing: "0.03333em",
                                                        }}
                                                    >
                                                        {el.name}
                                                    </Typography>
                                                </ListItem>
                                            ))}
                                        </List>
                                    )}
                            </Grid>
                        )
                        : (isAutocompleteMode
                            ? (
                                <Grid item xs={12}>
                                    <Typography
                                        sx={{
                                            margin: 0,
                                            opacity: 1,
                                            lineHeight: 1.25,
                                            color: "#7b809a",
                                            fontSize: "0.85rem",
                                            textTransform: "none",
                                            verticalAlign: "unset",
                                            letterSpacing: "0.03333em",
                                        }}
                                    >
                                        {strings.Common.NO_DATA}
                                    </Typography>
                                </Grid>
                            )
                            : (
                                <></>
                            )
                        )
                    }

                    {requestData.hasNext && (
                        <Grid item xs={12}>
                            <ListItem sx={(theme: Theme) => listItem(theme, { isAdd: true })} onClick={handleClickLoadMore}>
                                <RestartAlt sx={{ width: 22, height: 22, mr: 1 }} />
                                <Typography variant="button">{strings.Common.LOAD_MORE}</Typography>
                            </ListItem>
                        </Grid>
                    )}

                    {Helpers.isFunction(actionCreateNewItem) && (
                        <Grid item xs={12}>
                            <ListItem sx={(theme: Theme) => listItem(theme, { isAdd: true })} onClick={handleClickAddNewItem}>
                                <AddCircleOutline sx={{ width: 22, height: 22, mr: 1 }} />
                                <Typography variant="button">{strings.Common.ADD_NEW}</Typography>
                            </ListItem>
                        </Grid>
                    )}
                </Grid>
            )}
        </Box>
    );
};

export default SearchInputAPI;
